// First-touch attribution, shared with goaltree.ai through the gt_first_touch
// cookie on .goaltree.ai (apps/www/src/lib/posthog.ts writes the same shape).
const KEY = 'gt_first_touch'
const UTM = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const
const MAX_AGE = 6 * 30 * 24 * 60 * 60

export type FirstTouch = {
  referrer?: string
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  utmTerm?: string
  utmContent?: string
  gclid?: string
  fbclid?: string
  landingUrl?: string
  timestamp: number
}

type Raw = {
  referrer?: string
  utm?: Partial<Record<(typeof UTM)[number], string>>
  gclid?: string
  fbclid?: string
  landingUrl?: string
  timestamp?: number
}

const camel = (k: string) => k.replace(/_(\w)/g, (_, c: string) => c.toUpperCase())

function read(): Raw | null {
  const m = document.cookie.match(/(?:^|; )gt_first_touch=([^;]*)/)
  if (!m) return null
  try {
    return JSON.parse(decodeURIComponent(m[1])) as Raw
  } catch {
    return null
  }
}

// Visitors who land straight on the app (shared goal links, ads) never pass
// through goaltree.ai, so the app records its own first touch too.
export function captureFirstTouch() {
  if (typeof document === 'undefined' || read()) return
  const qs = new URLSearchParams(location.search)
  const utm = Object.fromEntries(UTM.flatMap((k) => (qs.get(k) ? [[k, qs.get(k)!]] : [])))
  // Our own pages (auth redirects, the marketing site) aren't a source.
  let host = ''
  try {
    host = new URL(document.referrer).host
  } catch {}
  const ref = host && !/goaltree\.ai|gt\.today/.test(host)
  const raw: Raw = {
    referrer: ref ? document.referrer : undefined,
    utm: Object.keys(utm).length ? utm : undefined,
    gclid: qs.get('gclid') ?? undefined,
    fbclid: qs.get('fbclid') ?? undefined,
    landingUrl: location.href,
    timestamp: Date.now(),
  }
  const domain = location.hostname.endsWith('goaltree.ai') ? '; domain=.goaltree.ai' : ''
  document.cookie = `${KEY}=${encodeURIComponent(JSON.stringify(raw))}; path=/${domain}; samesite=lax; max-age=${MAX_AGE}`
}

export function getFirstTouch(): FirstTouch | null {
  const raw = typeof document === 'undefined' ? null : read()
  if (!raw) return null
  const ft: FirstTouch = { timestamp: raw.timestamp ?? Date.now() }
  for (const [k, v] of Object.entries(raw.utm ?? {})) if (v) ft[camel(k) as 'utmSource'] = v
  for (const k of ['referrer', 'gclid', 'fbclid', 'landingUrl'] as const) if (raw[k]) ft[k] = raw[k]
  return ft
}
